import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { orgApi } from '../phase567Api';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';

export const PLAN_DIVISIONS = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'any', label: 'Any' },
];

// Ticket 202: edit a commission plan via PATCH /v2/org/commission-plans/{id}.
// Same fields as the New plan form. Saved commission records keep the numbers
// they were computed with, so an edit only changes records created afterwards.
export default function EditPlanDialog({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!plan) return;
    setForm({
      name: plan.name || '', division: plan.division || 'mortgage', bps: plan.bps ?? null,
      cap_per_deal: plan.cap_per_deal ?? null, deductions_pct: plan.deductions_pct ?? null,
    });
  }, [plan]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // Changed fields only, so an edit never rewrites a field it did not touch.
  const next = plan ? {
    name: String(form.name || '').trim(), division: form.division,
    bps: form.bps ?? null, cap_per_deal: form.cap_per_deal ?? null, deductions_pct: form.deductions_pct ?? 0,
  } : {};
  const was = plan ? {
    name: plan.name, division: plan.division, bps: plan.bps ?? null,
    cap_per_deal: plan.cap_per_deal ?? null, deductions_pct: plan.deductions_pct ?? 0,
  } : {};
  const body = Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== was[k]));
  const unchanged = Object.keys(body).length === 0;
  const save = async () => {
    setBusy(true);
    try {
      await orgApi.updatePlan(plan.plan_id, body);
      toast({ title: 'Plan saved' });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not save plan', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Edit commission plan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ''} onChange={(e) => set('name')(e.target.value)} className="mt-1" data-testid="plan-name" /></div>
          <PickSelect label="Division" value={form.division || 'mortgage'} onChange={set('division')} options={PLAN_DIVISIONS} testId="plan-division" />
          <div><Label>Basis Points (mortgage)</Label><NumberInput integer value={form.bps} onChange={set('bps')} className="mt-1" data-testid="plan-bps" /></div>
          <div><Label>Cap per Deal</Label><MoneyInput value={form.cap_per_deal} onChange={set('cap_per_deal')} className="mt-1" data-testid="plan-cap" /></div>
          <div><Label>Deductions %</Label><NumberInput value={form.deductions_pct} onChange={set('deductions_pct')} suffix="%" className="mt-1" data-testid="plan-deductions" /></div>
          <p className="text-xs text-slate-500">Records already saved keep the numbers they were computed with.</p>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={save} disabled={busy || unchanged || !String(form.name || '').trim()} className="btn-gold" data-testid="plan-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
